import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Tag, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Countdown from './Countdown';

const PAYMENT_URL = process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://myeaitradingcompass.com/trading-subscription';
const DISCOUNT_CODE = 'FLASH40';
const DISCOUNT_PERCENT = 40;

function PricingCard({ plan, discountApplied, onSelectPlan, index }) {
  const getDiscountedPrice = (price) => {
    if (discountApplied) {
      return Math.round(price * (1 - DISCOUNT_PERCENT / 100));
    }
    return price;
  };

  return (
    <motion.div
      data-testid={'pricing-card-' + plan.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className={
        'relative rounded-2xl border p-5 lg:p-6 bg-white ' +
        (plan.popular
          ? 'border-blue-400 shadow-lg ring-2 ring-blue-100'
          : 'border-slate-200 shadow-sm')
      }
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-blue-600 text-white rounded-full">
            <Sparkles className="h-3 w-3" /> MOST POPULAR
          </span>
        </div>
      )}

      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-1">{plan.name}</h3>
        <p className="text-xs text-slate-500">{plan.description}</p>
      </div>

      <div className="text-center mb-5">
        {discountApplied && (
          <span className="text-sm text-slate-400 line-through mr-2">
            ${plan.originalPrice}
          </span>
        )}
        <span className="text-3xl font-bold text-slate-900 font-[family-name:Space_Grotesk]">
          ${getDiscountedPrice(plan.originalPrice)}
        </span>
        <span className="text-sm text-slate-500 ml-1">/{plan.duration}</span>
      </div>

      <ul className="space-y-2 mb-5">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
            <Check className={'h-4 w-4 flex-shrink-0 ' + (plan.popular ? 'text-blue-600' : 'text-green-600')} />
            {feature}
          </li>
        ))}
      </ul>

      <Button
        data-testid={'select-plan-' + plan.id}
        onClick={onSelectPlan}
        className={
          'w-full rounded-xl font-semibold transition-all duration-200 ' +
          (plan.popular
            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
            : 'bg-slate-100 text-slate-900 hover:bg-slate-200 border border-slate-200')
        }
      >
        Get Started
      </Button>
    </motion.div>
  );
}

export default function PricingCards({ stripeUrl }) {
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(false);
  const [codeError, setCodeError] = useState('');

  const pricingPlans = [
    {
      id: 'starter',
      name: 'Starter',
      originalPrice: 29,
      duration: 'week',
      description: 'Perfect for beginners',
      features: ['Basic trade validation', '7-day access', 'Email support'],
      popular: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      originalPrice: 69,
      duration: '4 weeks',
      description: 'Most popular choice',
      features: ['Advanced trade analysis', '21-day full program', 'Priority support', 'AI risk insights'],
      popular: true,
    },
    {
      id: 'elite',
      name: 'Elite',
      originalPrice: 179,
      duration: '12 weeks',
      description: 'For serious traders',
      features: ['Lifetime access', 'Personal mentorship', '1-on-1 coaching calls', 'Advanced analytics', 'VIP community'],
      popular: false,
    },
  ];

  const handleApplyCode = () => {
    if (discountCode.toUpperCase() === DISCOUNT_CODE) {
      setDiscountApplied(true);
      setCodeError('');
    } else {
      setCodeError('Invalid code');
      setDiscountApplied(false);
    }
  };

  const handleSelectPlan = () => {
    const targetUrl = stripeUrl || PAYMENT_URL;
    window.open(targetUrl, '_blank');
  };

  return (
    <div data-testid="pricing-cards-section" className="mt-12">
      <div className="flex justify-center mb-6">
        <Countdown />
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-slate-600">Have a discount code?</span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            data-testid="discount-code-input"
            type="text"
            placeholder="Enter code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            className="w-32 h-9 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 text-sm"
          />
          <Button
            data-testid="apply-discount-button"
            onClick={handleApplyCode}
            variant="outline"
            size="sm"
            className="h-9 px-4 border-blue-300 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
          >
            Apply
          </Button>
        </div>
        {discountApplied && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            <Check className="h-4 w-4" /> 40% off applied!
          </span>
        )}
        {codeError && (
          <span className="text-sm text-red-600">{codeError}</span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto">
        {pricingPlans.map((plan, index) => (
          <PricingCard 
            key={plan.id}
            plan={plan}
            discountApplied={discountApplied}
            onSelectPlan={handleSelectPlan}
            index={index}
          />
        ))}
      </div>

      {!discountApplied && (
        <p className="text-center mt-4 text-sm text-slate-500">
          <span className="text-blue-600">Hint:</span> Try code <span className="text-blue-600 font-semibold">FLASH40</span> for 40% off!
        </p>
      )}
    </div>
  );
}
